'use client';

import * as React from 'react';
import * as T from '@radix-ui/react-tooltip';
import { cn } from './cn';

export const TooltipProvider = T.Provider;
export const TooltipRoot = T.Root;
export const TooltipTrigger = T.Trigger;

export interface TooltipProps
  extends Omit<React.ComponentPropsWithoutRef<typeof T.Content>, 'content'> {
  content: React.ReactNode;
  /** Rendered after the label, styled as key caps. */
  shortcut?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
  children: React.ReactNode;
}

/**
 * Hover/focus hint for icon-only controls.
 *
 * Tooltips never appear on touch (Radix ignores touch by design) — so a tooltip
 * is **always** an extra, never the only label. Pair every one with the matching
 * `aria-label` on `IconButton`.
 */
export function Tooltip({
  content,
  shortcut,
  open,
  defaultOpen,
  onOpenChange,
  delayDuration = 350,
  side = 'bottom',
  sideOffset = 6,
  className,
  children,
  ...props
}: TooltipProps) {
  return (
    <T.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} delayDuration={delayDuration}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            'z-(--z-tooltip) flex items-center gap-2 rounded-md bg-ink px-2.5 py-1.5',
            'text-xs font-medium text-ink-inverse shadow-md',
            'max-w-[min(88vw,18rem)] text-balance',
            'origin-(--radix-tooltip-content-transform-origin)',
            'data-[state=delayed-open]:animate-scale-in data-[state=closed]:animate-fade-out',
            className,
          )}
          {...props}
        >
          {content}
          {shortcut ? (
            <span dir="ltr" className="text-2xs font-mono text-ink-inverse/60">
              {shortcut}
            </span>
          ) : null}
          <T.Arrow className="fill-ink" width={10} height={5} />
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
